/**
 * Turning a document into the engine's own vocabulary.
 *
 * The document is already trusted by the time it reaches here: `./parse.ts` decides what a
 * document is allowed to say, and this decides what the engine is asked to do about it.
 */
import { array, field, group, record } from "../typed-form.js";
import { evaluateExpression, evaluateRuleCondition, expressionContextKeys, expressionPaths, } from "../expression.js";
import { validationMessagesForLocale } from "../validation-messages.js";
import { MDY_VALUE_CONTRACTS } from "../value-contracts.js";
import { eachOneOf, email, integer, completeRange, max, maxLength, min, minLength, crossField, oneOf, pattern, required, kindValidators, } from "../validators.js";
import { assertSafeDynamicName, isSafeDynamicSegment, MDY_MAX_DYNAMIC_PATH_LENGTH, MDY_MAX_DYNAMIC_PATTERN_LENGTH, warnDev, } from "./guards.js";
import { dynamicPatternRefusal } from "./pattern-cost.js";
import { mdyEmptyValueFor, } from "./schema.js";
/**
 * Maps the serializable validator set to validator functions.
 * Returns the functions plus whether the set marks the field required.
 */
export function buildDynamicValidators(config, 
/**
 * The language the refusals are written in. A form with an Italian label and an Italian calendar
 * refusing in English leaves the one line a person must read to get any further in a language they
 * did not ask for.
 */
locale) {
    const say = validationMessagesForLocale(locale);
    // The author's own words where the document wrote them, the form's language everywhere else.
    const wrote = config.messages ?? {};
    const out = [];
    if (config.required)
        out.push(required(wrote.required ?? say.required));
    if (config.email)
        out.push(email(wrote.email ?? say.email));
    if (config.integer)
        out.push(integer(wrote.integer ?? say.integer));
    if (config.min !== undefined) {
        out.push(min(config.min, wrote.min ?? say.min(config.min)));
    }
    if (config.max !== undefined) {
        out.push(max(config.max, wrote.max ?? say.max(config.max)));
    }
    if (config.minLength !== undefined) {
        out.push(minLength(config.minLength, wrote.minLength ?? say.minLength(config.minLength)));
    }
    if (config.maxLength !== undefined) {
        out.push(maxLength(config.maxLength, wrote.maxLength ?? say.maxLength(config.maxLength)));
    }
    if (config.pattern !== undefined) {
        if (config.pattern.length > MDY_MAX_DYNAMIC_PATTERN_LENGTH) {
            warnDev(`Skipped dynamic pattern validator: pattern length ${config.pattern.length} exceeds max ${MDY_MAX_DYNAMIC_PATTERN_LENGTH}.`, undefined, "MDY_DYNAMIC_PATTERN_TOO_LONG");
        }
        else {
            // Cost, on the same terms as syntax: a pattern whose shape backtracks exponentially stops the
            // field answering — the match is synchronous, so it is the whole thread — and a document is
            // not a place to accept that from. Refused the way an unparseable source is: a diagnostic and
            // no validator, rather than a form that half-works.
            const refusal = dynamicPatternRefusal(config.pattern);
            if (refusal !== null) {
                warnDev(`Skipped dynamic pattern validator: "${config.pattern}" has ${refusal}.`, undefined, "MDY_DYNAMIC_PATTERN_TOO_COSTLY");
            }
            else {
                try {
                    out.push(pattern(new RegExp(config.pattern), wrote.pattern ?? say.pattern));
                }
                catch {
                    warnDev(`Skipped dynamic pattern validator: invalid RegExp source "${config.pattern}".`, undefined, "MDY_DYNAMIC_INVALID_FIELD");
                }
            }
        }
    }
    return { validators: out, marksRequired: config.required === true };
}
/**
 * Builds the full validator set for one dynamic field: the configured
 * validators ({@link buildDynamicValidators}) plus, for option-based
 * kinds, an automatic whitelist of the declared option values — the
 * client-side anti-tampering guard ("select offers one/two → three is
 * invalid"). `select`/`radio`/`segmented` get `oneOf`, `multiselect` gets
 * `eachOneOf`. Prefer this over {@link buildDynamicValidators} whenever
 * the whole field config is available.
 */
/**
 * An option list a control cannot show is refused where the rules are compiled.
 *
 * `parseDynamicForm` already refuses a malformed list with `MDY_DYNAMIC_OPTIONS_REQUIRED`, and a
 * host assembling its own fields reaches this without passing through it. Left alone, a list of bare
 * strings compiled a rule that rejects every value — the option's `value` is `undefined`, so nothing
 * matches — and the sentence a person read was `Value must be one of: undefined, undefined`.
 *
 * Thrown rather than dropped, because there is no diagnostic channel on this path: the parser has
 * one and reports there, and this is the door taken by a caller who has no document to report about.
 */
/**
 * The sentence a refused choice reads, built from what the options are *called*.
 *
 * `oneOf` is handed values, and a value is what a document stores rather than what a person sees —
 * an id, a code. The label is the half the person can match against the list in front of them, and a
 * document that declared one has already said which words to use.
 */
function optionListMessage(options, subject = "Value") {
    if (options.length === 0)
        return "There are no choices to pick from.";
    const named = options.map((option) => option.label ?? String(option.value));
    return `${subject} must be one of: ${named.join(", ")}`;
}
/**
 * A bound written beside the field is a rule as well as a range.
 *
 * A number's limits can be said twice — `{ kind: "number", min: 0, max: 10 }` and
 * `{ validators: { min: 0, max: 10 } }` — and both render the same attributes on the control, so a
 * browser refuses what a person types either way. Only the second was a rule: a value that did not
 * come from the keyboard — a prefill, a restored draft, a scripted write — passed the first
 * untouched, and the form called itself valid holding it.
 *
 * The slider is where it shows: a value of 150 against `max: 50` left the form holding 150 and the
 * page showing the thumb at 50, with `aria-invalid="false"` and nothing said. The two spellings
 * render identically; only one of them meant anything.
 *
 * Read from the *field's* bound rather than from the control's drawn range. The range is already
 * derived from the rules (`VAL-004`: a native constraint never promises less than the validators it
 * came from), so deriving a rule from the range would close a loop — a rule from a range from a
 * rule — and the day the projection changes, the loop changes meaning with nothing to say so.
 *
 * The explicit `validators` entry wins where both are written: it is the narrower statement of
 * intent, and `mergeFacts` already takes whichever promises less when the two disagree.
 *
 * `step` has no rule to generate — the validator vocabulary has no `step` — so it stays what it
 * always was: an affordance at the keyboard, and nothing the form asserts.
 */
function withFieldBounds(field) {
    const declared = field.validators ?? {};
    if (field.kind !== "number" && field.kind !== "slider")
        return declared;
    return {
        ...declared,
        min: declared.min ?? field.min,
        max: declared.max ?? field.max,
    };
}
const KINDS_NEEDING_OPTIONS = new Set(["select", "radio", "multiselect", "segmented"]);
function assertUsableOptions(field) {
    if (!KINDS_NEEDING_OPTIONS.has(field.kind))
        return;
    // The list is missing entirely, which reached the compiler as `undefined.map` — an engine internal
    // on a caller's mistake, and the same absence the parser reports as `MDY_DYNAMIC_OPTIONS_REQUIRED`.
    if (Array.isArray(field.options) &&
        field.options.every((option) => typeof option === "object" && option !== null && "value" in option))
        return;
    throw new Error(`[modyra] The options for "${field.name ?? field.kind}" must each name a value: { value, label }. ` +
        "A list the contract cannot read compiles a rule no value satisfies.");
}
export function buildDynamicFieldValidators(field) {
    assertUsableOptions(field);
    // The field's own language, which a document declares beside the field it belongs to.
    const declared = buildDynamicValidators(withFieldBounds(field), field.locale);
    // Every kind guards its own shape, the same doorway `oneOf` guards for the kinds that have an
    // option list: a value from a restored draft or a scripted write is not the widget's own.
    const base = {
        ...declared,
        validators: [...declared.validators, ...kindValidators(field.kind)],
    };
    if (field.kind === "select" ||
        field.kind === "radio" ||
        field.kind === "segmented") {
        const values = field.options.map((option) => option.value);
        return {
            validators: [...base.validators, oneOf(values, optionListMessage(field.options))],
            marksRequired: base.marksRequired,
        };
    }
    if (field.kind === "multiselect") {
        const values = field.options.map((option) => option.value);
        return {
            validators: [
                ...base.validators,
                eachOneOf(values, optionListMessage(field.options, "Every value")),
            ],
            marksRequired: base.marksRequired,
        };
    }
    // A half-set range names no interval, so it is invalid whether or not the field is required —
    // the same way an option outside the declared set is invalid above. Leaving it to `required`
    // would mean an optional range could be submitted with a start and no end.
    if (field.kind === "daterange") {
        return {
            validators: [...base.validators, completeRange()],
            marksRequired: base.marksRequired,
        };
    }
    return base;
}
/**
 * Builds a form schema from the contract's **tree**, keeping its groups and arrays.
 *
 * {@link flattenDynamicSchema} answers a different question. It produces one flat list of dotted
 * names for a renderer that draws a sequence of controls, and in doing so it fixes each array at the
 * rows its initial value happened to have — which is correct for drawing and wrong for running,
 * because a row the user adds afterwards has no descriptor.
 *
 * Without this, the contract can *describe* a nested form that nothing can *instantiate*, and any
 * caller needing a live nested form has to read some other model instead. That is a gap in the
 * contract's runtime support, not a preference about how to build forms.
 */
/**
 * One child key of a document's tree, held to the rule a flat list's names are held to.
 *
 * A tree names one segment at a time and a flat list names a path, and both become the same widget
 * id. Without this, a name refused by `buildFlatFormSchema` was taken by this route — so which pair
 * of functions a consumer called decided whether their document worked.
 */
function assertSafeSegment(key) {
    if (!isSafeDynamicSegment(key)) {
        throw new Error(`[modyra] Invalid field name "${key}": every segment of a path must be present and must not ` +
            "be a prototype key, or the form would be keyed onto the prototype chain.");
    }
    assertSafeDynamicName(key);
}
/**
 * A document's condition, as the closure the typed slots already take.
 *
 * This is what makes the document half of ADR 0092 additive: `when` on a descriptor keeps its
 * signature, and what changes is that a document can now produce one. The expression is read
 * against **what encloses the clause** — the row for a cell inside a collection, the form for a
 * field at the root — which is the rule the typed `when` already follows; `{ self }` is the value
 * the clause is written on, and `{ root }` is the way out of a row.
 */
function conditionFrom(expression, context) {
    return (value, enclosing, root) => evaluateExpression(expression, enclosing, {
        self: value,
        root: root ?? enclosing,
        ...(context === undefined ? {} : { context }),
    });
}
/**
 * Every context key a document reads that the host did not supply.
 *
 * Read from the expressions rather than from what the document *declares* it needs: the declaration
 * is a promise to a reader, this is what the document does.
 */
function missingContextKeys(schema, context) {
    const missing = new Set();
    const pending = [schema];
    while (pending.length > 0) {
        const node = pending.pop();
        for (const clause of [
            node.when,
            node.asyncWhen,
        ]) {
            if (clause === undefined)
                continue;
            for (const key of expressionContextKeys(clause)) {
                if (context === undefined || !Object.hasOwn(context, key))
                    missing.add(key);
            }
        }
        if (node.node === "group")
            pending.push(...Object.values(node.children));
        else if (node.node === "array" || node.node === "record")
            pending.push(node.item);
    }
    return [...missing];
}
/**
 * Builds the form schema a parsed dynamic document describes.
 *
 * Expects the `schema` of a document `parseDynamicForm` accepted; the parser is the trust boundary.
 * The builder does not re-apply the parser's document limits: the hundred rows a collection may
 * write in its `initialValue` and the bound on what a document costs to flatten are both checked
 * there, so a tree that skips the parser is built whatever the rows it writes. What it does check
 * is per node: a field whose path is longer than `MDY_MAX_DYNAMIC_PATH_LENGTH` is dropped, and a
 * pattern longer than `MDY_MAX_DYNAMIC_PATTERN_LENGTH` builds no validator.
 */
export function buildDynamicFormSchema(schema, options = {}) {
    // The root of a parsed document, or nothing this can walk. `parseDynamicForm` is the door that
    // produces one; a caller arriving here with something else got a `TypeError` about `children` or
    // about converting null, which names neither the argument nor this call.
    if (typeof schema !== "object" || schema === null || Array.isArray(schema)) {
        throw new Error(`[modyra] buildDynamicFormSchema takes a document's root group node, received ${schema === null ? "null" : Array.isArray(schema) ? "an array" : `a ${typeof schema}`}. It is the document's own \`schema\`: buildDynamicFormSchema(document.schema).`);
    }
    // A group with nothing in it is the shape this refusal exists for — an empty document, or a
    // section someone left half-written — and it was the one shape that missed it: reading `children`
    // off an object that has none reached `Object.entries(undefined)` and raised an internal.
    const children = schema.children;
    if (typeof children !== "object" || children === null || Array.isArray(children)) {
        throw new Error("[modyra] buildDynamicFormSchema takes a document's root group node, and this one declares no " +
            "children. It is the document's own `schema`: buildDynamicFormSchema(document.schema).");
    }
    /**
     * Built bottom-up over an explicit stack, for the reason `validateDynamicSchema` is: a document is
     * untrusted, its nesting has no cap, and a recursive walk lets the document decide how much stack
     * to use. Overflowing is not a refusal a consumer can act on — it carries no path, cannot be
     * caught by name, and looks exactly like a bug in their own code.
     */
    // A key the document reads and the host does not supply, said here rather than answered `false`
    // for the life of the form.
    const missing = missingContextKeys(schema, options.context);
    if (missing.length > 0) {
        throw new Error(`[modyra] This document reads context ${missing.map((key) => `"${key}"`).join(", ")}, which ` +
            "the host did not supply. Pass it as buildDynamicFormSchema(schema, { context }); a condition " +
            "that cannot be read decides false, so the fields it guards would never appear.");
    }
    const built = new Map();
    /**
     * A field the path limit leaves out, so the group above it can tell "not built" from "built as
     * undefined". The parser drops the same field, and a form that built it held a value under a path
     * the contract calls unusable — in the payload and on no screen, because the renderer had nothing
     * to draw.
     */
    const OVER_THE_LIMIT = Symbol("mdy.path-too-long");
    const leaf = (node, name, path) => {
        if (path.length > MDY_MAX_DYNAMIC_PATH_LENGTH) {
            warnDev(`Dropped a dynamic field whose path is ${path.length} characters, past the ` +
                `${MDY_MAX_DYNAMIC_PATH_LENGTH} a path may be: a path is the payload key, the draft key ` +
                "and the widget id, and every read of the value carries it.", "/schema", "MDY_DYNAMIC_PATH_TOO_LONG");
            return OVER_THE_LIMIT;
        }
        const descriptor = { ...node.field, name };
        // `marksRequired` is not passed on: a `required()` validator in the list already raises the
        // field's own `required` signal, so the flag would be a second spelling of the same fact.
        const { validators } = buildDynamicFieldValidators(descriptor);
        // The one property in a document that names secrecy. Carried onto the descriptor so the form
        // itself knows, rather than leaving every protection to a list the application passes.
        const contract = MDY_VALUE_CONTRACTS[String(descriptor.kind)];
        return field(mdyEmptyValueFor(descriptor), validators, {
            sensitive: descriptor.sensitive === true,
            // The kind's own shape and, for a kind that chooses from a list, the values it offers: what a
            // value arriving from storage is held to. The flat builder declares the same two.
            ...(contract === undefined ? {} : { shape: contract.shape }),
            ...(Array.isArray(descriptor.options)
                ? {
                    options: descriptor.options
                        .map((option) => option.value),
                }
                : {}),
            ...(node.when === undefined ? {} : { when: conditionFrom(node.when, options.context) }),
            ...(node.asyncWhen === undefined
                ? {}
                : { asyncWhen: conditionFrom(node.asyncWhen, options.context) }),
        });
    };
    const build = (node, name) => {
        const pending = [
            { node, name, path: name, expanded: false },
        ];
        while (pending.length > 0) {
            const frame = pending[pending.length - 1];
            if (frame.node.node === "field") {
                built.set(frame.node, leaf(frame.node, frame.name, frame.path));
                pending.pop();
                continue;
            }
            if (!frame.expanded) {
                frame.expanded = true;
                if (frame.node.node === "group") {
                    for (const [key, child] of Object.entries(frame.node.children)) {
                        // A tree names one segment at a time and a flat list names a path, and both end up as
                        // the same widget id. Refused here too, so which pair of functions a consumer called
                        // does not decide whether their document works.
                        assertSafeSegment(key);
                        pending.push({ node: child, name: key, path: `${frame.path}.${key}`, expanded: false });
                    }
                }
                else {
                    // A row template has no path of its own: a row key is data, so what a cell is filed under
                    // is not known until a row exists. The declared path is what is measured here, and each
                    // row's own path is measured as it is flattened.
                    pending.push({ node: frame.node.item, name: frame.name, path: frame.path, expanded: false });
                }
                continue;
            }
            pending.pop();
            if (frame.node.node === "group") {
                const children = {};
                for (const [key, child] of Object.entries(frame.node.children)) {
                    const child_ = built.get(child);
                    if (child_ === OVER_THE_LIMIT)
                        continue;
                    children[key] = child_;
                }
                // Every child gone and the document declaring some: the group described one thing, that thing
                // is past the limit, and a group with nothing in it is not what the document said. It goes
                // the same way its children did, so the refusal reaches the level a consumer can see.
                if (Object.keys(children).length === 0 && Object.keys(frame.node.children).length > 0) {
                    built.set(frame.node, OVER_THE_LIMIT);
                    continue;
                }
                built.set(frame.node, group(children, frame.node.when === undefined
                    ? undefined
                    : { when: conditionFrom(frame.node.when, options.context) }));
                continue;
            }
            const item = built.get(frame.node.item);
            // A collection whose row shape is past the limit has no row shape, so it is not a collection
            // the document can have — it goes the way its item did.
            if (item === OVER_THE_LIMIT) {
                built.set(frame.node, OVER_THE_LIMIT);
                continue;
            }
            if (frame.node.node === "record") {
                // Same template idea as an array's item: one row shape, whatever key it ends up under.
                built.set(frame.node, record(item, {
                    ...(frame.node.initialValue !== undefined ? { initial: frame.node.initialValue } : {}),
                }));
                continue;
            }
            // The item descriptor is the template every row is built from, which is what keeps a pushed
            // row identical to an initial one.
            const validators = [];
            if (frame.node.minItems !== undefined)
                validators.push(minLength(frame.node.minItems));
            if (frame.node.maxItems !== undefined)
                validators.push(maxLength(frame.node.maxItems));
            built.set(frame.node, array(item, {
                ...(frame.node.initialValue !== undefined ? { initial: frame.node.initialValue } : {}),
                ...(validators.length ? { validators } : {}),
            }));
        }
        return built.get(node);
    };
    const root = {};
    for (const [key, child] of Object.entries(schema.children)) {
        assertSafeSegment(key);
        const node = build(child, key);
        if (node === OVER_THE_LIMIT)
            continue;
        root[key] = node;
    }
    return root;
}
/**
 * Turns a document's `rules` into the bindings the engine already has for them.
 *
 * The sibling of {@link buildDynamicValidations}: the parser reads both slots as behaviour — it
 * refuses an undeclared effect, an undeclared operator and a target that is not a field — and one of
 * them had no way to become behaviour. A document that said "hide the VAT number unless the customer
 * is a business" parsed, was accepted in strict mode, and produced a form that showed it always.
 *
 * Applied rather than returned, because a rule is a binding on a named field and not a value a
 * caller could hold: `visible`/`hidden` take a field out of play, which is what `setInactive` means,
 * and `enabled`/`disabled` reach `setDisabled`. Out of play and disabled are different promises —
 * a field out of play is not asked for at all, a disabled one is asked for and not answered — and
 * the four effects are two pairs over exactly that difference.
 *
 * One signal per field per effect, however many rules name it: the engine's binding replaces rather
 * than accumulates, so composing here is what stops the last rule silently winning.
 */
export function applyDynamicRules(form, rules) {
    const inactive = new Map();
    const disabled = new Map();
    for (const rule of rules) {
        const into = rule.effect === "visible" || rule.effect === "hidden" ? inactive : disabled;
        const held = into.get(rule.target);
        if (held)
            held.push(rule);
        else
            into.set(rule.target, [rule]);
    }
    /**
     * Whether the rules that name one field, taken together, leave it out.
     *
     * The two effects are not each other's negation once there is more than one rule. A positive rule
     * is a **way in**: "show this for a business" and "show this for a charity" are alternatives, so
     * any one of them holding is enough. A negative rule is a **veto**: "hide it while the account is
     * closed" holds whatever else is true.
     *
     * Composing both sides as "off if any says off" reads the positives as conditions that must all
     * hold — the opposite of what two of them mean — and a field with two `visible` rules is then
     * shown to nobody. So the positives are `some` over *in*, the negatives are `some` over *out*, and
     * a veto beats a way in.
     */
    const isOut = (own, value) => {
        const vetoed = own.some((rule) => (rule.effect === "hidden" || rule.effect === "disabled") && evaluateRuleCondition(rule.when, value));
        if (vetoed)
            return true;
        const ways = own.filter((rule) => rule.effect === "visible" || rule.effect === "enabled");
        return ways.length > 0 && !ways.some((rule) => evaluateRuleCondition(rule.when, value));
    };
    for (const [target, own] of inactive) {
        form.setInactive(target, form.reactivity.computed(() => isOut(own, form.value())));
    }
    for (const [target, own] of disabled) {
        form.setDisabled(target, form.reactivity.computed(() => isOut(own, form.value())));
    }
}
export function buildDynamicValidations(validations) {
    return validations.map((validation) => {
        const paths = validation.target !== undefined ? [validation.target] : expressionPaths(validation.when);
        return crossField(paths, (value) => (evaluateExpression(validation.when, value) ? validation.message : null));
    });
}
